import {
  Box, Button, Flex, Grid, GridItem, Text,
} from "@chakra-ui/react";

import { useForm } from "react-hook-form";
import { CustomToast, Dropdown, FormInput } from "../../../../../components";
import { useEffect, useState } from "react";
import { useFetch } from "../../../../../hooks/useFetch";
import { IBasicInformation } from "../../modal";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

interface Props {
  nextStep: () => void;
  prevStep: () => void;
  basicInfo: IBasicInformation;
  setBasicInfo: (data: IBasicInformation) => void;
  leadId: number;
}

export const BasicInformationSchema = yup.object().shape({
  company_name: yup.string().trim().required("Company Name is required"),
  companyPhone: yup.string().trim().required("Phone Number is required").matches(/^[0-9]{10,15}$/, "Enter a valid phone number"),
  websiteUrl: yup.string().trim().nullable().notRequired().test(
    "is-url",
    "Enter a valid website URL",
    (value) => !value || /^https?:\/\/.+/i.test(value)
  ),
  countryId: yup.number().typeError("Country is required").required("Country is required"),
  sourceId: yup.number().typeError("Lead Source is required").required("Lead Source is required"),
  agencyPocId: yup
    .number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined ? null : value
    )
    .when("sourceId", {
      is: (val: any) => Number(val) === 16,
      then: (schema) =>
        schema
          .typeError("Agency POC is required")
          .moreThan(0, "Agency POC is required")
          .required("Agency POC is required"),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  industrytypeId: yup.number().typeError("Industry Type is required").required("Industry Type is required"),
  contactPersonName: yup.string().trim().required("Contact Person Name is required"),
  companySize: yup.string().nullable().notRequired(),
  socialLinks: yup.array().of(
    yup.object().shape({
      socialLink: yup.string().nullable().test("is-url", "Enter a valid social link", (value) => !value || /^https?:\/\/.+/i.test(value)
      ),
    })
  ),
  companyEmail: yup.string().trim().required("Email Address is required").email("Enter a valid email address"),
  designationId: yup.number().typeError("Designation is required").required("Designation is required"),
});


const BasicInformation = ({ nextStep, prevStep, basicInfo, setBasicInfo, leadId }: Props) => {
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);

  const [industryTypes, setIndustryTypes] = useState<any[]>([]);
  const [countryData, setCountryData] = useState<any[]>([]);
  const [sourceDropDown, setSourceDropDown] = useState<any[]>([]);
  const [agencyPocDropDown, setAgencyPocDropDown] = useState<any[]>([]);
  const [designationData, setDesignationData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLinkFields, setSocialLinkFields] = useState(() => {
    if (basicInfo?.socialLinks?.length) {
      return basicInfo.socialLinks.map((_: any, index: number) => index);
    }
    return [0];
  });
  const { control, reset, handleSubmit, watch, setValue, formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(BasicInformationSchema),
    defaultValues: basicInfo,
  });

  const sourceIdWatch = watch("sourceId");

  const onSubmit = async (data: IBasicInformation) => {
    setIsLoading(true);
    const payload = {
      leadId,
      ...data,
      agencyPocId: Number(data.sourceId) === 16 ? data.agencyPocId || null : null,
    };
    const res = await fetchApi(
      "LeadRequirement/LeadReqBasicInfo",
      "POST",
      payload
    );

    if (res) {
      setBasicInfo({
        ...data,
        agencyPocId: Number(data.sourceId) === 16 ? data.agencyPocId || null : null,
      });
      nextStep();
    }
    setIsLoading(false);
  };

  const loadIndustryTypes = async () => {
    const res = await fetchApi("Dropdown/IndustryType", "GET");
    if (res) {
      setIndustryTypes(res);
    }
  };

  const loadCountryDropDown = async () => {
    const res = await fetchApi("Dropdown/country", "GET");
    if (res) {
      setCountryData(res);
    }
  };

  const loadSource = async () => {
    const res = await fetchApi("Dropdown/source", "GET");
    if (res) {
      setSourceDropDown(res);
    }
  };

  const loadAgencyPoc = async () => {
    const res = await fetchApi("Dropdown/agency-poc", "GET");
    if (res) {
      const list = Array.isArray(res) ? res : (res.data || []);
      const formatted = list.map((x: any) => ({
        ...x,
        agencyPocId: Number(x.agencyPocId ?? x.AgencyPocId ?? x.id),
        displayName: x.displayName ?? x.DisplayName ?? `${x.agencyName || x.AgencyName || ''} || ${x.agencyPocName || x.AgencyPocName || x.name || ''}`,
      }));
      setAgencyPocDropDown(formatted);
    }
  };

  const loadDesignationDropDown = async () => {
    const res = await fetchApi("Dropdown/designations", "GET");
    if (res) {
      setDesignationData(res);
    }
  };

  useEffect(() => {
    loadIndustryTypes();
    loadCountryDropDown();
    loadSource();
    loadAgencyPoc();
    loadDesignationDropDown();
  }, [leadId]);

  useEffect(() => {
    if (Number(sourceIdWatch) === 16) {
      if (agencyPocDropDown.length === 0) {
        loadAgencyPoc();
      }
    } else {
      setValue("agencyPocId", null);
    }
  }, [sourceIdWatch]);


  useEffect(() => {
    if (basicInfo) {
      reset({
        ...basicInfo,
        socialLinks:
          basicInfo.socialLinks?.length > 0
            ? basicInfo.socialLinks
            : [{ socialLink: "" }],
      });

      setSocialLinkFields(
        basicInfo.socialLinks?.length > 0
          ? basicInfo.socialLinks.map((_: any, index: number) => index)
          : [0]
      );
    }
  }, [basicInfo, reset]);

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr", }} gap="24px 32px">
          {/* LEFT SIDE */}
          <GridItem>
            <Box mb={5}>
              <FormInput isRequired control={control} name="company_name" type="string" label="Company Name" placeholder="Enter Company Name" errors={errors} />
            </Box>

            <Box mb={5}>
              <FormInput control={control} name="websiteUrl" type="string" label="Website URL" placeholder="Enter Website URL" errors={errors} />
            </Box>

            <Box mb={5}>
              <Dropdown isRequired control={control} name='industrytypeId' label="Industry Type" options={industryTypes} valueKey='industryId' labelKey='industryName' errors={errors} />
            </Box>

            <Box mb={5}>
              <FormInput isRequired control={control} name="contactPersonName" type="string" label="Contact Person Name" placeholder="Enter Contact Person Name" errors={errors} />
            </Box>

            <Box mb={5}>
              <Dropdown isRequired control={control} name='designationId' label='Designation' options={designationData} valueKey='designationId' labelKey='designationName' errors={errors} />
            </Box>

            <Box>
              <FormInput isRequired control={control} name="companyEmail" type="string" label="Email Address" placeholder="Enter Email Address" errors={errors} />
            </Box>
          </GridItem>

          {/* RIGHT SIDE */}
          <GridItem>
            <Box mb={5}>
              <FormInput isRequired control={control} name="companyPhone" type="string" label="Phone Number" placeholder="Enter Phone Number" errors={errors} />
            </Box>

            <Box mb={5}>
              <Dropdown isRequired control={control} name='countryId' label='Country' options={countryData} valueKey='countryId' labelKey='countryName' errors={errors} />
            </Box>

            <Box mb={5}>
              <Dropdown isRequired control={control} name='sourceId' label='Lead Source' options={sourceDropDown} valueKey='sourceId' labelKey='sourceName' errors={errors} />
            </Box>

            {Number(sourceIdWatch) === 16 && (
              <Box mb={5}>
                <Dropdown
                  isRequired
                  control={control}
                  name="agencyPocId"
                  label="Agency POC"
                  options={agencyPocDropDown}
                  valueKey="agencyPocId"
                  labelKey="displayName"
                  placeholder="Select Agency POC"
                  errors={errors}
                />
              </Box>
            )}

            <Box mb={5}>
              <FormInput control={control} name="companySize" type="string" label="Company Size" placeholder="Enter Company Size" errors={errors} />
            </Box>

            <Box>
              <Flex align="center" gap={1} mb={2}>
                <Text fontSize="13px" fontWeight="600"> Social Links </Text>

                <Text fontSize="11px" color="#6B7280">
                  (optional)
                </Text>
              </Flex>

              {socialLinkFields.map((_: number, index: number) => (
                <Box key={index} mb={3}>
                  <FormInput control={control} name={`socialLinks.${index}.socialLink`} type="string" label="" placeholder={`Enter Social Link ${index + 1}`} errors={errors} />
                </Box>
              ))}

              <Text mt={2} fontSize="12px" color="#4F46E5" cursor="pointer" textAlign="right" onClick={() => setSocialLinkFields([...socialLinkFields, socialLinkFields.length])}
              >
                + Add more Social Links
              </Text>
            </Box>
          </GridItem>
        </Grid>

        {/* FOOTER BUTTONS */}
        <Flex justify="flex-end" gap={3} mt={10}>
          {/* <Button bg="#6B7280" color="#FFFFFF" _hover={{ bg: "#4B5563", }} onClick={prevStep}>
            Back
          </Button> */}

          <Button type="submit" bg="#4F46E5" color="#FFFFFF" _hover={{ bg: "#4338CA", }} isLoading={isLoading}>
            Next
          </Button>
        </Flex>
      </form>
    </Box>
  );
};

export default BasicInformation;